/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ReactComponent as UploadIcon } from "@assets/Icons/Icon_Upload_file.svg";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericModal from "@ui-components/Modal/GenericModal";
import { TicketPriorityTag, TicketStatusTag } from "../SupportFields";
import { Attachment, TicketDetail } from "../TicketDetail";
import { LoadingWrapper } from "kontrolEdge/components/Shared/LoadingWrapper";
import {
  SkeletonAvatar,
  SkeletonLine,
  SkeletonText,
} from "@ui-components/Skeleton/GenericSkeleton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { AttachmentList } from "./AttachmentUI";
import { withFallback } from "kontrolEdge/helpers/formattersShappedData";
import { getStatusText } from "../RequestData";

type Props = {
  isOpen: boolean;
  ticketId: string | number | null;
  fetchTicketById: (id: string | number) => Promise<TicketDetail>;
  onClose: () => void;
  //Hooks
  onDownloadAttachment?: (
    ticketId: string | number,
    att: Attachment
  ) => Promise<void>;
  onSendReply?: (
    ticketId: string | number,
    message: string,
    files?: File[]
  ) => Promise<void>;
  isKontrolEdge?: boolean;
};

export default function TicketDetailsModal({
  isOpen,
  ticketId,
  fetchTicketById,
  onClose,
  onDownloadAttachment,
  onSendReply,
  isKontrolEdge = false,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [replyText, setReplyText] = useState("");
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [downloadingMap, setDownloadingMap] = useState<
    Record<string | number, boolean>
  >({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isSending, setIsSending] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  

  // Fetch details whenever the modal opens with a ticketId
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!isOpen || ticketId == null) return;
      setLoading(true);
      setTicket(null);
      try {
        const data = await fetchTicketById(ticketId);
        if (!cancelled) setTicket(data);
      } catch (e) {
        console.error("Ticket detail fetch failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [isOpen, ticketId, fetchTicketById]);

  const handleOpenFilePicker = () => fileInputRef.current?.click();
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) setReplyFiles(Array.from(e.target.files));
  };
  const handleRemoveAttachment = (idx: number) =>
    setReplyFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleDownload = async (att: Attachment) => {
    if (!onDownloadAttachment || ticketId == null) return;
    setDownloadingMap((prev) => ({ ...prev, [att.id]: true }));
    try {
      await onDownloadAttachment(ticketId, att);
    } finally {
      setDownloadingMap((prev) => ({ ...prev, [att.id]: false }));
    }
  };

  //Added to do the refetch in the modal
  const convContainerRef = useRef<HTMLDivElement>(null);
  const handleSend = async () => {
    if (!onSendReply || ticketId == null) return;
    const message = replyText.trim();
    if (!message || isSending) return;
    try {
      setIsSending(true);
      await onSendReply(ticketId, message, replyFiles);
      // Re-fetch latest ticket so the new comment shows up
      const updated = await fetchTicketById(ticketId);
      setTicket(updated);
      // clear inputs
      setReplyText("");
      setReplyFiles([]);
      // success toast/alert
      setSuccessOpen(true);
      // scroll to newest
      queueMicrotask(() => {
        const el = convContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to send reply. Please try again.");
      setErrorOpen(true);
    } finally {
      setIsSending(false);
    }
  };

  const isFriendlyObj = (
    v: unknown
  ): v is { friendly?: string; iso8601?: string } =>
    !!v &&
    typeof v === "object" &&
    ("friendly" in (v as any) || "iso8601" in (v as any));

  const toFriendlyUnknown = (v: unknown): string => {
    if (isFriendlyObj(v)) return v.friendly || v.iso8601 || "";
    return typeof v === "string" ? v : "";
  };

  // Helper to display name + cleaned body when "incoming: true" and name is "Customer Support"
  const getDisplayFromConversation = (
    conv: any
  ): { displayName: string; bodyText: string } => {
    const baseName = (conv?.sender ?? conv?.name ?? "").trim();
    const isCustomerSupport = /customer support/i.test(baseName);
  
    if (isCustomerSupport) {
      return {
        displayName: "",
        bodyText: (conv?.body_text ?? "").trim(),
      };
    }
    return {
      displayName: baseName || "Unknown",
      bodyText: conv?.body_text ?? "",
    };
  };

  // -------- Skeletons ----------
  const TicketDetailsSkeleton = () => (
    <div className="space-y-6 my-5">
      {/* Top pills row */}
      <div className="flex items-center gap-4 flex-wrap">
        <SkeletonLine w="120px" h={20} />
        <SkeletonLine w="100px" h={20} />
        <SkeletonLine w="140px" h={20} />
      </div>

      {/* Description */}
      <div className="space-y-5">
        <SkeletonLine w="30%" h={18} />
        <div className="border border-gray-300 rounded p-2">
          <SkeletonText lines={4} widths="random" />
        </div>
      </div>
      {/* Conversations list (3 items) */}
      <div className="space-y-5">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="pb-2 border-b border-gray-200 last:border-b-0 pl-2"
          >
            <div className="flex items-center gap-3 mb-1">
              <SkeletonAvatar size={28} />
              <SkeletonLine w="180px" h={16} />
            </div>
            <SkeletonText lines={2} randomRange={[60, 95]} />
          </div>
        ))}
      </div>
    </div>
  );

  const handleModalClose = useCallback(() => {
    if (isSending) return;
    onClose();
  }, [isSending, onClose]);

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={ticket ? `Ticket: ${ticket.subject}` : "Ticket Details"}
      size="lg"
    >
      <div
        style={{ maxHeight: "80vh", overflowY: "auto", paddingRight: "2vw" }}
      >
        <LoadingWrapper
          isLoading={loading}
          skeleton={<TicketDetailsSkeleton />}
        />
        {!loading && ticket && (
          <div className="space-y-5 text-sm text-gray-800">
            {/* Top pills: priority, status, date */}
            <div className="flex items-center gap-4 flex-wrap">
              <div>
                <strong>Priority:</strong>{" "}
                {
                  Number.isFinite(Number(ticket?.priority))
                    ? <TicketPriorityTag value={Number(ticket?.priority)} />
                    : withFallback(null)
                }
              </div>
              <div>
                <strong>Status:</strong>{" "}
                {
                  (() => {
                    const st = getStatusText(ticket?.status as any);
                    return st !== "-" ? <TicketStatusTag value={st} /> : "—";
                  })()
                }
              </div>
              {ticket.date?.friendly && (
                <div>
                  <strong>Date:</strong> {withFallback(ticket?.date?.friendly)}
                </div>
              )}
              <div>
                <strong>Service ID:</strong>{withFallback(ticket?.service_id)}
              </div>
            </div>
            <div>
              <strong>Reporter: </strong>
              {`${withFallback(ticket?.requester?.name)} (${withFallback(ticket?.requester?.email)})`}
            </div>

            {/* Description */}
            {ticket.description_text && (
              <div>
                <h3 className="font-semibold text-base mb-1">Description:</h3>
                <div className="border border-gray-300 rounded p-2 max-h-40 overflow-y-auto whitespace-pre-line leading-5">
                  {ticket.description_text}
                </div>
              </div>
            )}

            {/* Custom fields */}
            {ticket.custom_fields?.cf_tipo && (
              <div className="flex flex-wrap gap-6">
                <div>
                  <strong>Tipo:</strong> {String(ticket.custom_fields.cf_tipo)}
                </div>
              </div>
            )}

            {/* Conversations */}
            <div>
              <h3 className="font-semibold text-base mb-1">Conversations:</h3>
              <div
                ref={convContainerRef}
                className="border border-gray-200 rounded p-2 max-h-60 overflow-y-auto divide-y divide-gray-300"
              >
                {ticket.conversations?.length ? (
                  ticket.conversations.map((conv, idx) => {
                    const { displayName, bodyText } =
                      getDisplayFromConversation(conv);
                    const showName = Boolean(displayName && displayName.trim());
                    const hasBody = Boolean(bodyText?.trim());
                    const isFromTeam = conv.incoming === false;
                    const textColorClass = isFromTeam
                      ? "text-blue-600"
                      : "text-green-600";
                    const bgColorClass = isFromTeam
                      ? "bg-gray-50"
                      : "bg-[#EDEDED]";
                    const createdFriendly =
                      toFriendlyUnknown(conv.created_at_friendly) ||
                      toFriendlyUnknown(conv.created_at);
                    const atts: Attachment[] = Array.isArray(conv.attachments)
                      ? conv.attachments
                      : [];
                    const hasAtts = atts.length > 0;

                    // Special distribution when NO name and NO body:
                    if (!showName && !hasBody) {
                      return (
                        <div
                          key={idx}
                          className={`py-4 border-b border-gray-400 last:border-b-0 px-2 ${bgColorClass} m-0`}
                        >
                          {hasAtts ? (
                            <div className="mt-1">
                              <div className="flex items-center justify-between mb-2 text-xs">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-bold uppercase tracking-wide text-gray-600">
                                    Attachment files
                                  </h4>
                                  <span className="text-[11px] text-gray-500">({atts.length})</span>
                                </div>
                                <span className="font-semibold text-gray-500">{createdFriendly}</span>
                              </div>

                              <AttachmentList
                                attachments={atts}
                                downloadingMap={downloadingMap}
                                onDownload={handleDownload}
                                compact
                                columns={2}
                              />
                            </div>
                          ) : (
                            // No name, no body, no attachments → just the date.
                            <div className="text-xs text-gray-500 flex justify-end mb-2">
                              <span className="font-semibold" >Ticket created at: {createdFriendly} </span>
                            </div>
                          )}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={idx}
                        className={`py-4 border-b border-gray-400 last:border-b-0 px-2 ${bgColorClass} m-0`}
                      >
                        <div className="mb-5 text-xs text-gray-500 flex items-center justify-between">
                          {showName && (
                            <span className={`text-md font-bold whitespace-pre-line ${textColorClass}`}>
                              {displayName}
                            </span>
                          )}
                          <span className="font-semibold">
                            {withFallback(createdFriendly)}
                          </span>
                        </div>

                        {bodyText?.trim() && (
                          <p className="text-sm whitespace-pre-line">{bodyText}</p>
                        )}


                        {atts.length > 0 && (
                          <div className="mt-4 pt-3">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="text-xs font-bold uppercase tracking-wide text-gray-600">
                                Attachment files
                              </h4>
                              <span className="text-[11px] text-gray-500">
                                ({atts.length})
                              </span>
                            </div>

                            <AttachmentList
                              attachments={atts}
                              downloadingMap={downloadingMap}
                              onDownload={handleDownload}
                              compact
                              columns={2}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p>No conversations yet.</p>
                )}
              </div>
            </div>

            {/* Ticket-level attachments */}
            {!!ticket.attachments?.length && (
              <div className="mb-4 mt-2 border-t border-gray-200 pt-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">
                  Ticket Attachments
                </h3>
                <AttachmentList
                  attachments={ticket.attachments}
                  downloadingMap={downloadingMap}
                  onDownload={handleDownload}
                  columns={2}
                  oneVisibleRow
                />
              </div>
            )}

            {/* Reply */}
            <div className="mt-4">
              <label
                className="font-semibold text-base mb-1"
                htmlFor="reply-input"
              >
                Reply:
              </label>
              <textarea
                id="reply-input"
                className="w-full h-24 p-2 border border-gray-300 rounded-sm text-sm"
                placeholder="Write your reply here..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
              />

              {isKontrolEdge && (
                <div className="mt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      accept="*/*"
                    />
                    <GenericButton
                      variant="secondary"
                      size="sm"
                      label="Add Attachments"
                      icon={<UploadIcon className="w-4 h-4" />}
                      iconPosition="left"
                      onClick={handleOpenFilePicker}
                    />
                    <span className="text-sm text-gray-500">
                      {replyFiles.length} file(s) selected
                    </span>
                  </div>

                  {replyFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {replyFiles.map((file, i) => (
                        <GenericTag
                          key={i}
                          label={file.name}
                          variant="default"
                          size="small"
                          removable
                          onRemove={() => handleRemoveAttachment(i)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end">
                <GenericButton
                  label="Send Reply"
                  variant="brand"
                  onClick={handleSend}
                  disabled={!replyText.trim() || !onSendReply}
                  loading={isSending}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <GenericAlert
        isOpen={successOpen}
        onClose={() => setSuccessOpen(false)}
        onConfirm={() => setSuccessOpen(false)}
        variant="success"
        title="Reply sent"
        description="Your message was delivered successfully."
        confirmLabel="OK"
      />

      <GenericAlert
        isOpen={errorOpen}
        onClose={() => setErrorOpen(false)}
        onConfirm={() => setErrorOpen(false)}
        variant="error"
        title="Could not send reply"
        description={errorMsg || "Something went wrong. Please try again."}
        confirmLabel="Close"
      />
    </GenericModal>
  );
}
